import { addDays, addMonths, format, isAfter, isSameDay, startOfDay, subMonths, subWeeks } from "date-fns";
import {
  ConfidenceLevelData,
  EvidencePart,
  IUserProgramCertificate,
  ProgressCheckSubmission,
  SubmissionState,
} from "@interfaces/Interfaces";
import DOMPurify from "dompurify";
import * as ProgramUtils from "./programUtils";
import { PROGRAM_IDS } from "@constants/programs";

export function checkStringIsEmptyAfterTrimming(string: string): boolean {
  return string.trim().length === 0;
}

export function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function getCommentsDisclaimerText(
  programID: string,
  mentorNamingConvention: string | undefined
): string | undefined {
  // console.log(programID);
  switch (programID) {
    case PROGRAM_IDS.SWAST_NQP2:
    case PROGRAM_IDS.NEAS_NQP:
    case PROGRAM_IDS.LAS_NQP:
      return `This is not a private record. Every ${mentorNamingConvention?.toLowerCase()} you work with will see all your comments, as well as your other managers.`;
    case PROGRAM_IDS.NWAS_NQP:
      return "This is not a private record. Your SP will see all your comments.";
    case PROGRAM_IDS.YAS_NQP:
      return "This is not a private record. All mentors you work with will see your comments, as well as your TL and other managers.";
    default:
      return "This is not a private record. All preceptors you work with will see all your comments.";
  }
}

export function getProgressCheckSubmissionChangedByHeader(status: string | undefined): string {
  switch (status) {
    case SubmissionState.Approved:
      return "Approved by:";
    case SubmissionState.OnHoldReset:
      return "Put On Hold (Reset) by";
    case SubmissionState.OnHold:
      return "Put On Hold by:";
    default:
      return "";
  }
}

export function getProgressCheckStatusText(status: string | undefined): string {
  switch (status) {
    case SubmissionState.Approved:
      return SubmissionState.Approved;
    case SubmissionState.Completed:
      return SubmissionState.Completed;
    case SubmissionState.OnHoldReset:
      return "On Hold (Reset)";
    case SubmissionState.OnHold:
      return "On Hold";
    case SubmissionState.Submitted:
      return SubmissionState.Submitted;
    case SubmissionState.InProgress:
      return "In Progress";
    default:
      return "Not started";
  }
}

export function getProgressCheckPDFCompletedText(submissionState: ProgressCheckSubmission | undefined): string {
  switch (submissionState?.status) {
    case SubmissionState.Approved:
      return "Approved on:";
    case SubmissionState.Completed:
      return "Completed on:";
    case SubmissionState.OnHoldReset:
      return "Progress Check reset on:";
    case SubmissionState.OnHold:
      return "Progress Check held on:";
    case SubmissionState.Submitted:
      return "Submitted on:";
    default:
      return "Status updated on: ";
  }
}

export function getRoleDisplayString(role: string): string {
  switch (role) {
    case "Mentee":
      return "";
    case "Mentor":
      return " - Line Manager";
    case "OO":
      return " - OO (Operational Officer)";
    case "OO (Operational Officer)":
      return " - OO (Operational Officer)";
    case "EL (Educational Lead)":
      return " - EL (Educational Lead)";
    default:
      return ` - ${role}`;
  }
}

export function getAuditTickboxTitle(): string {
  return "Mark this for audit";
}

export function getAuditTickboxText(): string {
  return "e.g. HCPC, clinical review, other";
}

/**
 * Retrieves the container class for the certificate expiry date pill
 * @param certificate - The program certificate
 * @param duration - How long the certificate is valid for in months
 * @param canBeBlank - Whether the certificate can have its issue date omitted
 * @returns The tailwind class string
 */
export function getCertificatePillContainerClass(
  certificate: IUserProgramCertificate | undefined | null,
  duration: string | undefined,
  canBeBlank?: boolean
): string {
  const durationNumber = duration ? parseInt(duration, 10) : 12;

  if (certificate?.issueDate) {
    const issueDate = new Date(certificate.issueDate);
    const today = new Date();

    // Expiry date is 1 year from issue date
    const expiry = addMonths(issueDate, durationNumber);
    // Red date is 6 weeks before expiry, amber date is 4 months before expiry
    const redDate = subWeeks(expiry, 6);
    const amberDate = subMonths(expiry, 4);

    if (isAfter(today, new Date(expiry))) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white";
    } else if (isAfter(today, redDate)) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white";
    } else if (isAfter(today, amberDate)) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-[#FEEBC8] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#9C4221]";
    } else {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-[#C6F7C1] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#085333]";
    }
  } else if (canBeBlank) {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-transparent text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-cta-blue";
  } else {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-[#FCBAAC] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#890012]";
  }
}

export function getRedDateThreshold(params: { programID?: string; expiry: Date }): Date {
  const { programID, expiry } = params;

  switch (programID) {
    case PROGRAM_IDS.NWAS_ARC:
      return addDays(expiry, 1);
    case PROGRAM_IDS.NWAS_EPRR:
      return addDays(expiry, 1);
    case PROGRAM_IDS.SWAST_EPRR:
      return addDays(expiry, 1);
    default:
      return subWeeks(expiry, 6);
  }
}

export function getAmberDateThreshold(params: { programID?: string; expiry: Date }): Date {
  const { programID, expiry } = params;

  switch (programID) {
    case PROGRAM_IDS.NWAS_ARC:
      return subMonths(expiry, 3);
    case PROGRAM_IDS.NWAS_EPRR:
      return subMonths(expiry, 3);
    case PROGRAM_IDS.SWAST_EPRR:
      return subMonths(expiry, 3);
    default:
      return subMonths(expiry, 4);
  }
}

export type SwastNwasEprrCertificateRagStatus = "green" | "amber" | "red";

const SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND = {
  green:
    "shrink-0 h-[20px] py-0 px-[8px] bg-[#C6F7C1] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#085333]",
  amber:
    "shrink-0 h-[20px] py-0 px-[8px] bg-[#FEEBC8] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#9C4221]",
  red: "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white",
  blank:
    "shrink-0 h-[20px] py-0 px-[8px] bg-transparent text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-cta-blue",
  none: "shrink-0 h-[20px] py-0 px-[8px] bg-[#FCBAAC] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#890012]",
} as const;

const SWAST_NWAS_EPRR_CERTIFICATE_PILL_CLASS_NAME: Record<SwastNwasEprrCertificateRagStatus, string> = {
  green: "programCardCertificatePillGreen",
  amber: "programCardCertificatePillAmber",
  red: "programCardCertificatePillExpired",
};

export function getSwastNwasEprrCertificateRagStatus(today: Date, expiry: Date): SwastNwasEprrCertificateRagStatus {
  const todayDay = startOfDay(today);
  const expiryDay = startOfDay(expiry);
  const amberStart = startOfDay(subMonths(expiry, 3));

  if (isSameDay(todayDay, expiryDay) || isAfter(todayDay, expiryDay)) {
    return "red";
  }

  if (isAfter(todayDay, amberStart)) {
    return "amber";
  }

  return "green";
}

export function isSwastNwasEprrCertificateExpired(expiry: Date, today: Date = new Date()): boolean {
  return getSwastNwasEprrCertificateRagStatus(today, expiry) === "red";
}

export function getSwastNwasEprrCertificatePillContainerClassNoExpiry(params: {
  certificate: IUserProgramCertificate | undefined | null;
  duration: string | undefined;
  canBeBlank?: boolean;
}): string {
  const { certificate, duration, canBeBlank } = params;

  if (certificate?.issueDate) {
    if (duration && duration !== "0") {
      const expiry = addMonths(new Date(certificate.issueDate), parseInt(duration, 10));
      const status = getSwastNwasEprrCertificateRagStatus(new Date(), expiry);
      return SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND[status];
    }

    return SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND.green;
  }

  if (canBeBlank) {
    return SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND.blank;
  }

  return SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND.none;
}

export function getSwastNwasEprrCertificatePillContainerClassName(params: {
  certificate: IUserProgramCertificate | undefined;
  duration: string;
  canBeBlank?: boolean;
}): string {
  const { certificate, duration, canBeBlank } = params;
  const durationNumber = parseInt(duration, 10) || 12;

  if (certificate?.issueDate) {
    const expiry = addMonths(new Date(certificate.issueDate), durationNumber);
    const status = getSwastNwasEprrCertificateRagStatus(new Date(), expiry);
    return SWAST_NWAS_EPRR_CERTIFICATE_PILL_CLASS_NAME[status];
  }

  if (canBeBlank) {
    return "programCardCertificatePillBlank";
  }

  return "programCardCertificatePillNone";
}

export function getSwastNwasEprrCertificatePillContainerClassFromExpiryDate(expiry: Date): string {
  const status = getSwastNwasEprrCertificateRagStatus(new Date(), expiry);
  return SWAST_NWAS_EPRR_CERTIFICATE_PILL_TAILWIND[status];
}

/**
 * Retrieves the container class for the certificate expiry date pill
 * @param certificate - The program certificate
 * @param duration - How long the certificate is valid for in months
 * @param canBeBlank - Whether the certificate can have its issue date omitted
 * @returns The tailwind class string
 */
export function getCertificatePillContainerClassNoExpiry(params: {
  certificate: IUserProgramCertificate | undefined | null;
  duration: string | undefined;
  canBeBlank?: boolean;
  programID?: string;
}): string {
  const { certificate, duration, canBeBlank, programID } = params;

  if (certificate?.issueDate) {
    let expiry;

    const today = new Date();

    if (duration && duration !== "0") {
      const durationNumber = parseInt(duration, 10);
      expiry = addMonths(new Date(certificate.issueDate), durationNumber);
    }

    if (!expiry) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-[#C6F7C1] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#085333]";
    }

    // Red date is 6 weeks before expiry, amber date is 4 months before expiry
    const redDate = getRedDateThreshold({ programID, expiry });
    const amberDate = getAmberDateThreshold({ programID, expiry });

    if (!isSameDay(today, expiry) && isAfter(today, new Date(expiry))) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white";
    } else if (isAfter(today, redDate)) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white";
    } else if (isAfter(today, amberDate)) {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-[#FEEBC8] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#9C4221]";
    } else {
      return "shrink-0 h-[20px] py-0 px-[8px] bg-[#C6F7C1] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#085333]";
    }
  } else if (canBeBlank) {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-transparent text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-cta-blue";
  } else {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-[#FCBAAC] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#890012]";
  }
}
/**
 * Retrieves the container class for the certificate expiry date pill
 * @param expiry - The expiry date of the certificate
 * @returns The tailwind class string
 */
export function getCertificatePillContainerClassFromExpiryDate(expiry: Date): string {
  const today = new Date();

  if (isAfter(today, new Date(expiry))) {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-cta-red rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-white";
  } else {
    return "shrink-0 h-[20px] py-0 px-[8px] bg-[#C6F7C1] rounded-[10px] text-[13px] font-semibold leading-[1.54] tracking-[-0.05px] text-right text-[#085333]";
  }
}

/**
 * Retrieves the text for the add certificate button
 * @param certificate - The program certificate
 * @param duration - How long the certificate is valid for in months
 * @param canBeBlank - Whether the certificate can have its issue date omitted
 * @returns
 */
export function getCertificatePillString(
  certificate: IUserProgramCertificate | undefined | null,
  duration: string | undefined,
  canBeBlank?: boolean
): string {
  const durationNumber = duration ? parseInt(duration, 10) : 12;

  if (certificate?.issueDate) {
    const expiry = addMonths(new Date(certificate.issueDate), durationNumber);
    return format(expiry, "d MMM yy");
  } else if (canBeBlank) {
    return "Add certificate";
  } else {
    return "No certificate";
  }
}

export function getCertificatePillStringNoExpiry(
  certificate: IUserProgramCertificate | undefined | null,
  duration: string | undefined,
  canBeBlank?: boolean,
  defaultButtonText?: string
): string {
  if (certificate?.issueDate) {
    let expiry;

    if (duration && duration !== "0") {
      const durationNumber = parseInt(duration, 10);
      expiry = addMonths(new Date(certificate.issueDate), durationNumber);
    }

    return expiry ? format(expiry, "d MMM yy") : "Does not expire";
  } else if (canBeBlank) {
    return defaultButtonText ? `Add ${defaultButtonText.toLowerCase()}` : "Add certificate";
  } else {
    return defaultButtonText ? `No ${defaultButtonText.toLowerCase()}` : "No certificate";
  }
}

export function getCertificateDurationString(duration: string): string {
  if (!duration) {
    return "Does not expire";
  }

  const durationNumber = parseInt(duration, 10);

  if (durationNumber === 0) {
    return "Does not expire";
  }

  if (durationNumber % 12 === 0) {
    return `${durationNumber / 12} year${durationNumber / 12 > 1 ? "s" : ""}`;
  }

  return `${durationNumber} month${durationNumber > 1 ? "s" : ""}`;
}

/**
 * Returns a string representing the activity duration. If the duration is not present, return an empty string.
 * If the duration is present and has two dates, format it as "dd MMM yyyy to dd MMM yyyy". If the duration is present and has one date, format it as "dd MMM yyyy".
 * @param activityDate - The activity duration, which can be a string with one or two dates separated by a semicolon
 * @returns A string representing the activity duration
 */
export function getActivityDateString(activityDate?: string | undefined, activityDateEnd?: string | undefined): string {
  if (!activityDate) return "";

  if (activityDate && activityDateEnd) {
    return `${format(new Date(activityDate), "d MMMM yyyy")} to ${format(new Date(activityDateEnd), "d MMMM yyyy")}`;
  } else if (activityDate) {
    return format(new Date(activityDate), "d MMMM yyyy");
  }

  return "";
}

export function getActivityDurationDisplayString(duration: string) {
  const [hours, minutes] = duration === "" ? ["0000", "00"] : duration.split(":");
  return `${hours} hr : ${minutes.padStart(2, "0")} min`;
}

/**
 * Returns the correct title string for the competence card header
 * @param {string} programID - The ID of the program
 * @returns The correct title string
 */
export function getCompetenceCardTitle(programID: string): string {
  if (ProgramUtils.isMTS(programID) || ProgramUtils.isCHUB(programID) || ProgramUtils.isClinicalAdvisor(programID)) {
    return "Evidence type";
  }

  return "Required evidence";
}

/**
 *
 * @param active - Whether the user has pieces of evidence with the confidence levels added
 * @returns The correct subtitle string for the Confidence Levels card header
 */
export function getConfidenceLevelsHeader(
  confidenceLevelData: ConfidenceLevelData | null,
  programName?: string,
  programID?: string
): string {
  if (confidenceLevelData === null) {
    switch (programID) {
      case PROGRAM_IDS.SWAST_PRECEPTORSHIP:
        return `During this ${programName} program, you are required to assess and record your confidence level 3 times. Choose 2 topics to track from the ‘Start-point’ Progress check onwards.`;
      case PROGRAM_IDS.NEAS_NQP:
        return `During this ${programName} program, you are required to assess and record your confidence level 4 times. Choose 2 topics to track from the ‘0-6 months’ Progress check onwards.`;
      case PROGRAM_IDS.YAS_NQP:
        return `During this ${programName} program, you are required to assess and record your confidence level 4 times. Choose 2 topics to track from the ‘0-6 months’ Progress check onwards.`;
      case PROGRAM_IDS.LAS_NQP:
        return `During this ${programName} program, you are required to assess and record your confidence level 4 times. Choose 2 topics to track from the ‘6-month Review’ Progress check onwards.`;
      default:
        return `During this ${programName} program, you are required to assess and record your confidence level 3 times. Choose 2 topics to track from the ‘Start-point’ Progress check onwards.`;
    }
  }

  return `Your confidence levels throughout this ${programName} program.`;
}

export function getErrorBannerHeaderFieldsText(length: number): string {
  return `${length} mandatory field${length > 1 ? "s" : ""}`;
}

export function getErrorBannerText(length: number): string {
  return `${length > 1 ? "have" : "has"} not been completed`;
}

/**
 * Sanitizes an input HTML string to use in dangerouslySetInnerHTML to prevent XSS
 * @param {string} html - The  string to sanitize
 * @returns An object { __html: string }
 */
export function sanitizeHTML(html: string, allowedAttributes: string[] = ["target"]): { __html: string } {
  return { __html: DOMPurify.sanitize(html, { ADD_ATTR: allowedAttributes }) };
}

export const DOT_SEPARATOR = "\u00A0•\u00A0";

export function getDisplayTitleForPDFExport(evidencePart: EvidencePart): string {
  const title = (evidencePart["Display Title"] ?? evidencePart.Name ?? "").trim();

  if (!title) {
    return "";
  }

  if (title.endsWith(":")) {
    return title;
  }

  return `${title}:`;
}

export function escapeCSV(value: string | number | boolean | null | undefined): string {
  if (value === null || value === undefined) return "";

  const stringValue = String(value);
  // Replace curly quotes with straight quotes for Excel compatibility
  const withStraightQuotes = stringValue.replace(/[""]/g, '"');
  const normalized = withStraightQuotes.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Quote if contains comma, quote, or newline; escape internal quotes
  if (/[",\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }

  return normalized;
}

export function capitalizeFirstLetter(string: string): string {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export function toSlug(string: string): string {
  return string
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}
